import { ArrowRightOutline18Icon } from "@vygruppen/spor-icon-react";
import { format } from "date-fns";
import { FilterData } from "features/History/Filter/FilterData";
import { TrainButton } from "features/History/HistoryDetail/components/TrainButton";
import { getEventState } from "features/History/HistoryDetail/HistoryDetail";
import { useOperationalInformation } from "features/History/useOperationalInformation";
import {
  CustomResponsiveTr,
  CustomText,
  HistoryCustomTableStyle,
} from "features/History/utils/HistoryCustomTableStyle";
import {
  eventIsInfrastructureEvent,
  eventIsStationEvent,
  eventIsTrainEvent,
  getDropsDistributionText,
  getSubTitleFromAffectedStops,
  groupLabels,
} from "features/History/utils/utils";
import { FC, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { FailureMessage } from "shared/components/feedback/FailureMessage/FailureMessage";
import { TableSkeletonHistory } from "shared/components/feedback/SkeletonLoader/TableSkeleton";
import {
  DropsBadge,
  DropsButton,
  HStack,
  TableBody,
  TableCell,
  TableColumnHeader,
  TableFooter,
  TableHeader,
  Text,
  Tooltip,
  VStack,
} from "shared/components/Spor";
import { VisibilityIndicator } from "shared/icons/VisibilityIndicator";
import { CombinedHistoryResponse } from "shared/types/operationalInformation";

type OperationalEventProps = {
  filter: FilterData;
  setFilter: (filter: FilterData) => void;
  onSelectEvent: (event: CombinedHistoryResponse) => void;
};

export const OperationalEvents: FC<OperationalEventProps> = ({
  filter,
  setFilter,
  onSelectEvent,
}) => {
  const { data, status } = useOperationalInformation(
    filter.countryCode,
    filter.date.toString(),
  );

  const { t } = useTranslation();

  useEffect(() => {
    if (!data) return;
    const newAllTrainIds = data
      .filter(eventIsTrainEvent)
      .map((event) => event.trainIdentifier.identifier);
    const newFilter = filter.setAllTrainIds(newAllTrainIds);
    const allTrainIdsJSON = JSON.stringify(filter.allTrainIds);
    const newAllTrainIdsJSON = JSON.stringify(newFilter.allTrainIds);
    if (allTrainIdsJSON !== newAllTrainIdsJSON) {
      setFilter(newFilter);
    }
  }, [data, filter]);

  const [showAllEvents, setShowAllEvents] = useState(false);

  const filteredData = data?.filter((event) => filter.isMatch(event));

  const displayedMinimized = 6;

  const displayedEvents = showAllEvents
    ? filteredData
    : filteredData?.slice(0, displayedMinimized);

  return (
    <HistoryCustomTableStyle>
      <TableHeader>
        <CustomResponsiveTr>
          <TableColumnHeader>{t("Versjonsdato")}</TableColumnHeader>
          <TableColumnHeader>{t("Type")}</TableColumnHeader>
          <TableColumnHeader>{t("Hendelse", { count: 1 })}</TableColumnHeader>
          <TableColumnHeader className="DontShowIfSmallWidth">
            {t("Versjon")}
          </TableColumnHeader>
          <TableColumnHeader>{t("Tilstand")}</TableColumnHeader>
          <TableColumnHeader
            // minWidth is used here to make TableSkeleton follow the width more correctly
            minWidth="200px"
          >
            <HStack justifyContent="space-between" mr="10px">
              <Text>{t("Siste tekst")}</Text>
              <HStack>
                <Text>{filteredData?.length}</Text>
                <CustomText fontWeight="normal">{t("treff")}</CustomText>
              </HStack>
            </HStack>
          </TableColumnHeader>
        </CustomResponsiveTr>
      </TableHeader>

      <TableBody>
        {status === "pending" && (
          <TableSkeletonHistory numOfRows={1} numOfColumns={6} />
        )}
        {status === "error" && <FailureMessage />}
        {status === "success" &&
          displayedEvents?.map((event) => {
            const eventState = getEventState(event.state, event.type.type, t);
            return (
              <CustomResponsiveTr
                key={event.uuid}
                onClick={() => {
                  onSelectEvent(event);
                }}
              >
                <TableCell>
                  <Text fontWeight="bold">
                    {format(new Date(event.updatedAt), "dd.MM.yyyy")}
                  </Text>
                  {format(new Date(event.updatedAt), "HH:mm")}
                </TableCell>
                <TableCell>
                  <Text fontWeight="bold">
                    {groupLabels(t)[event.type.group]}
                  </Text>
                  <TrainButton event={event} />
                </TableCell>
                <TableCell>
                  <VStack align="start">
                    <Text fontWeight="bold" lineClamp="3">
                      {event.type.label ?? event.type.type}
                    </Text>
                    {eventIsInfrastructureEvent(event) && event.stretchName && (
                      <Text lineClamp="2">{event.stretchName}</Text>
                    )}

                    {eventIsStationEvent(event) &&
                      event.stops &&
                      event.stops.length > 0 && (
                        <Text lineClamp="2">
                          {getSubTitleFromAffectedStops(event.stops)}
                        </Text>
                      )}
                    {eventIsTrainEvent(event) &&
                      event.affectedStops &&
                      event.affectedStops.length > 0 && (
                        <Text lineClamp="2">
                          {getSubTitleFromAffectedStops(event.affectedStops)}
                        </Text>
                      )}
                  </VStack>
                </TableCell>
                <TableCell className="DontShowIfSmallWidth">
                  <CustomText>{event.version}</CustomText>
                </TableCell>
                <TableCell>
                  <HStack justifyContent="space-between">
                    <DropsBadge semantics={eventState.semantics}>
                      {eventState.label}
                    </DropsBadge>
                    <Tooltip
                      hasArrow
                      placement="right"
                      label={event.isVisible ? t("Synlig") : t("Usynlig")}
                    >
                      <VisibilityIndicator
                        semantics={event.isVisible ? "success" : "alarm"}
                      />
                    </Tooltip>
                  </HStack>
                </TableCell>
                <TableCell>
                  <HStack
                    justifyContent="space-between"
                    alignItems="flex-start"
                    paddingRight="12px"
                  >
                    <Text
                      style={{
                        // Behaviour: Use max 3 lines and show "..." at overflow
                        overflow: "hidden",
                        display: "-webkit-box",
                        WebkitLineClamp: "3",
                        WebkitBoxOrient: "vertical",
                      }}
                    >
                      {getDropsDistributionText(event.distributions)}
                    </Text>

                    <DropsButton
                      mt="-6px"
                      rightIcon={<ArrowRightOutline18Icon />}
                      flexShrink={0}
                      variant="ghost"
                      size="xs"
                      onClick={() => onSelectEvent(event)}
                      id="seedetails"
                    >
                      <CustomText>{t("Se detaljer")}</CustomText>
                    </DropsButton>
                  </HStack>
                </TableCell>
              </CustomResponsiveTr>
            );
          })}
      </TableBody>
      <TableFooter>
        <CustomResponsiveTr>
          <TableCell
            gridColumn="1 / -1"
            textAlign="right"
            fontWeight="normal"
            fontSize={16}
          >
            <HStack justifyContent="flex-end" pr="12px">
              <Text fontSize="xs">
                {displayedEvents && filteredData
                  ? t("Viser {{count}} av {{total}}", {
                      count: displayedEvents.length,
                      total: filteredData.length,
                    })
                  : t("Laster...")}{" "}
              </Text>
              {filteredData && filteredData.length > displayedMinimized && (
                <DropsButton
                  variant="tertiary"
                  size="sm"
                  ml="12px"
                  onClick={() => setShowAllEvents(!showAllEvents)}
                >
                  {showAllEvents ? t("Vis mindre") : t("Vis alle")}
                </DropsButton>
              )}
            </HStack>
          </TableCell>
        </CustomResponsiveTr>
      </TableFooter>
    </HistoryCustomTableStyle>
  );
};
